import { useState, useEffect, useRef } from 'react';
import { usePrefersReducedMotion } from './usePrefersReducedMotion';

function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

interface UseAnimatedCounterOptions {
  /** Target numeric value. */
  target: number;
  /** Duration in ms. Default: 2000 */
  duration?: number;
  /** Prefix string (e.g. '$'). */
  prefix?: string;
  /** Suffix string (e.g. '%', 'B'). */
  suffix?: string;
  /** Final display override (e.g. '5-15%', 'LOWEST'). Shown once animation completes. */
  display?: string;
  /** Whether to start the animation. Typically bound to isVisible from useIntersectionObserver. */
  enabled: boolean;
}

/**
 * Animated number counter with easeOutCubic easing.
 * Replaces initCounters() from app.js.
 * Returns the formatted display string.
 */
export function useAnimatedCounter(options: UseAnimatedCounterOptions): string {
  const { target, duration = 2000, prefix = '', suffix = '', display, enabled } = options;
  const prefersReduced = usePrefersReducedMotion();
  const [text, setText] = useState(display ? '' : `${prefix}0${suffix}`);
  const hasAnimated = useRef(false);

  useEffect(() => {
    if (!enabled || hasAnimated.current) return;

    if (prefersReduced) {
      setText(display ?? `${prefix}${target}${suffix}`);
      hasAnimated.current = true;
      return;
    }

    hasAnimated.current = true;
    const start = performance.now();

    function tick(now: number) {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const value = Math.round(easeOutCubic(progress) * target);

      if (display && progress >= 1) {
        setText(display);
      } else {
        setText(`${prefix}${value}${suffix}`);
      }

      if (progress < 1) {
        requestAnimationFrame(tick);
      }
    }

    requestAnimationFrame(tick);
  }, [enabled, target, duration, prefix, suffix, display, prefersReduced]);

  return text;
}
